/**
 * 日期范围筛选器组件
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useIntl } from 'umi';
import classNames from 'classnames';
import moment, { Moment } from 'moment';
import RangePicker from '@/pages/home/components/RangePicker';
import RangePickerLeft from '@/pages/dashboard/components/RangePickerLeft';
import { getExtraShortcut } from '@/pages/dashboard/components/NewFilter/FilterBase/getExtraShortcut';
import { START_OF_DATE } from '@/pages/dashboard/utils/_config/constant';
import { IDateRangeProps, TDateRangeValue } from './types';
import { getLocaleState } from '@home/utils/state';
import { DATALAB_DATE_EVENT } from '@dbl/core/global';
import type { TDatalabDateEvent } from '@dbl/core/global';
import './index.scss';

const START_OF_WEEK = 1;

// 获取默认日期范围：过去7天（从7天前到昨天）
const getDefaultDateRange = (): [Moment, Moment] => {
  const yesterday = moment().subtract(1, 'day');
  const sevenDaysAgo = moment().subtract(7, 'day');
  return [sevenDaysAgo, yesterday];
};

// 获取截止日期（昨天）
const getLatestDate = () => {
  const yesterday = moment().subtract(1, 'day');
  return {
    date: yesterday,
  };
};

const DateRange: React.FC<IDateRangeProps> = (props) => {
  const { className, title, required, value, onChange } = props;
  const { locale } = useIntl();
  const isEn = locale === 'en-US';

  // 默认值：过去7天
  const defaultDateRange = useMemo(() => getDefaultDateRange(), []);
  const latestDate = useMemo(() => getLatestDate(), []);
  const startOfTime = START_OF_DATE;
  const { data: seasonInfo } = getLocaleState<TDatalabDateEvent>(DATALAB_DATE_EVENT) || {};
  
  // RangePickerLeft 需要 latestDate 对象，但接口定义不准确，使用类型断言
  const latestDateForShortcut = latestDate as any;

  const [dateValue, setDateValue] = useState<TDateRangeValue>(
    value || defaultDateRange
  );

  // 同步外部传入的 value 到内部状态
  useEffect(() => {
    if (value !== undefined) {
      setDateValue(value);
    }
  }, [value]);

  const handleChange = (newValue: [Moment | null, Moment | null] | null): void => {
    setDateValue(newValue);
    onChange?.(newValue);
  };

  return (
    <div className={classNames('dbl-date-range', className)}>
      {title && (
        <div
          className={classNames('dbl-date-range-title', {
            'is-required': required,
            'dbl-date-range-title-en': isEn,
          })}
        >
          {title}
        </div>
      )}
      <div className="dbl-date-range-picker-wrapper search-item-date">
        <RangePicker
          type="date"
          latestDate={latestDate}
          value={dateValue}
          startOfTime={startOfTime}
          setShortcutOption={() => (
            <RangePickerLeft
              latestDate={latestDateForShortcut}
              startOfTime={startOfTime}
              seasonInfo={seasonInfo}
              activeTab="common"
              handleChangeValue={() => {}}
              setSelectLeftType={() => {}}
              selectedLeftType="common"
              childRef={React.createRef()}
              type="date"
              dateFilterKey="normal"
            />
          )}
          className="dbl-date-range-picker dtb-rangepicker-label-wrap"
          overlayClassName="dbl-date-range-picker-overlay dbd-range-picker-overlay"
          typeList={['date']}
          showSelectType={false}
          startOfWeek={START_OF_WEEK}
          language={isEn ? 'en' : 'cn'}
          onChange={handleChange}
          defaultValue={{
            date: defaultDateRange,
          }}
          extraShortcut={getExtraShortcut(seasonInfo)}
        />
      </div>
    </div>
  );
};

export default DateRange;
